import {
  BuildingOfficeIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InboxArrowDownIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { t } from 'i18next';

import { MIN_SEARCH_LENGTH } from 'support/helpers/filters/filters';
import { isDefined } from 'support/helpers/generic/generic';
import { TransactionFlowState, TransactionState } from 'support/types';
import { TransactionFilterKey } from 'support/types/filterKeys';
import { PropertyFilter, PropertyFilterType, SearchPropertyFilter } from 'support/types/filters';
import { buildDocumentTypeFilterWithVirtual } from 'components/Filters/helpers';

export const getDefaultFilterConfig = (
  hasReconciliation: boolean | undefined,
): Array<PropertyFilter | SearchPropertyFilter> => [
  searchFilter(),
  buildDocumentTypeFilterWithVirtual(TransactionFilterKey.transactionType),
  partnerFilter(),
  stateFilter(hasReconciliation),
  flowFilter(),
  sourceFilter(),
  dateFilter(),
];

export const getBlockedTransactionsFilterConfig = (): Array<PropertyFilter | SearchPropertyFilter> => [
  searchFilter(),
  buildDocumentTypeFilterWithVirtual(TransactionFilterKey.transactionType),
  partnerFilter(),
  flowFilter(TransactionFlowState.live),
  actionRequiredFilter(),
  dateFilter(),
];

const searchFilter = () =>
  ({
    key: TransactionFilterKey.transactionId,
    type: PropertyFilterType.search,
    placeholder: t('transactions:list.filter.searchByExternalReference.placeholder'),
    minLength: MIN_SEARCH_LENGTH,
    label: '',
    value: '',
  }) as SearchPropertyFilter;

const partnerFilter = () => ({
  key: TransactionFilterKey.partnerId,
  type: PropertyFilterType.dropdown,
  label: t('transactions:list.filter.partnerOptions.label'),
  Icon: BuildingOfficeIcon,
  value: '',
  dropDownOptions: {
    dropdownValues: [],
    multiple: true,
  },
  eventDetails: {
    event: 'filter_changed',
    properties: {
      filter_type: 'partner',
    },
  },
});

const stateFilter = (hasReconciliation: boolean | undefined) => {
  return {
    key: TransactionFilterKey.transactionState,
    type: PropertyFilterType.dropdown,
    label: t('transactions:list.filter.messageStateOptions.label'),
    Icon: CheckCircleIcon,
    value: '',
    dropDownOptions: {
      dropdownValues: [
        {
          label: t('transactions:list.filter.messageStateOptions.options.inProgress'),
          value: TransactionState.pending,
        },
        { label: t('transactions:list.filter.messageStateOptions.options.delivered'), value: TransactionState.success },
        { label: t('transactions:list.filter.messageStateOptions.options.dropped'), value: TransactionState.dropped },
        { label: t('transactions:list.filter.messageStateOptions.options.failed'), value: TransactionState.failed },
        hasReconciliation
          ? {
              label: t('transactions:list.filter.messageStateOptions.options.missing'),
              value: TransactionState.missing,
            }
          : null,
        {
          label: t('transactions:list.filter.messageStateOptions.options.pendingReview'),
          value: TransactionState.pending_review,
        },
        {
          label: t('transactions:list.filter.messageStateOptions.options.superseded'),
          value: TransactionState.superseded,
        },
        { label: t('transactions:list.filter.messageStateOptions.options.unknown'), value: TransactionState.unknown },
      ].filter(isDefined),
    },
    eventDetails: {
      event: 'filter_changed',
      properties: {
        filter_type: 'transaction_state',
      },
    },
  };
};

const flowFilter = (defaultValue: TransactionFlowState | '' = '') => ({
  key: TransactionFilterKey.transactionFlow,
  type: PropertyFilterType.dropdown,
  value: defaultValue,
  label: t('transactions:list.filter.messageFlowOptions.label'),
  Icon: ServerStackIcon,
  dropDownOptions: {
    multiple: true,
    dropdownValues: [
      { label: t('transactions:list.filter.messageFlowOptions.options.live'), value: TransactionFlowState.live },
      { label: t('transactions:list.filter.messageFlowOptions.options.test'), value: TransactionFlowState.test },
    ],
  },
  eventDetails: {
    event: 'filter_changed',
    properties: {
      filter_type: 'transaction_flow',
    },
  },
});

const dateFilter = () => ({
  key: TransactionFilterKey.transactionCreatedBetween,
  type: PropertyFilterType.date,
  label: t('transactions:list.filter.date.label'),
  placeholder: t('transactions:list.filter.date.label'),
  Icon: CalendarIcon,
  value: '',
  eventDetails: {
    event: 'filter_changed',
    properties: {
      filter_type: 'transaction_date',
    },
  },
});

const sourceFilter = () => ({
  key: TransactionFilterKey.originApp,
  type: PropertyFilterType.dropdown,
  label: t('transactions:list.filter.sourceOptions.label'),
  Icon: InboxArrowDownIcon,
  value: '',
  dropDownOptions: {
    dropdownValues: [
      { label: t('transactions:list.filter.sourceOptions.options.orderAgent'), value: 'DOCUMENT_UNDERSTANDING_AGENT' },
      { label: t('transactions:list.filter.sourceOptions.options.orderForm'), value: 'ORDER_FORM' },
      { label: t('transactions:list.filter.sourceOptions.options.webEdi'), value: 'PORTAL_WEB_EDI' },
      { label: t('transactions:list.filter.sourceOptions.options.portalOcr'), value: 'PORTAL_OCR' },
      { label: t('transactions:list.filter.sourceOptions.options.unattendedOcr'), value: 'UNATTENDED_OCR' },
    ],
  },
  eventDetails: {
    event: 'filter_changed',
    properties: {
      filter_type: 'source',
    },
  },
});

const actionRequiredFilter = () => ({
  key: TransactionFilterKey.actionRequired,
  type: PropertyFilterType.dropdown,
  label: t('transactions:list.filter.actionRequired.label'),
  Icon: ExclamationCircleIcon,
  value: 'true',
  dropDownOptions: {
    dropdownValues: [
      { label: t('connections:list.filter.actionRequired.required.label'), value: 'true' },
      { label: t('connections:list.filter.actionRequired.notRequired.label'), value: 'false' },
    ],
  },
});
